import { keepPreviousData, useQuery } from '@tanstack/react-query'

import type { UseQueryResult } from '@tanstack/react-query'
import type { AxiosRequestConfig } from 'axios'
import type { ITemplate, ITemplateParams } from '~/types/template'

import { getTemplate, getTemplateVersions, getTemplates } from '~/api/dynamic-screen'

////////// KEYS //////////
export const templatesKeys = {
  all: ['templates'] as const,
  details: (key: string, version: string) => [...templatesKeys.all, 'details', key, version] as const,
  list: (params?: AxiosRequestConfig['params']) => [...templatesKeys.all, 'list', params] as const,
  versions: (key: string) => [...templatesKeys.all, 'versions', key] as const,
}

////////// QUERIES //////////
export const useTemplateListQuery = (
  params: AxiosRequestConfig['params'],
  settings?: { enabled?: boolean }
): UseQueryResult<IPaginatedResponse<ITemplate>> => {
  return useQuery({
    placeholderData: keepPreviousData,
    queryFn: () => getTemplates(params),
    queryKey: templatesKeys.list(params),
    ...settings,
  })
}

export const useTemplateDetailsQuery = (
  { deviceType, isUnity, key, version }: ITemplateParams,
  settings: { enabled?: boolean }
): UseQueryResult<ITemplate> & { isLoadingAndEnabled: boolean } => {
  const query = useQuery({
    placeholderData: keepPreviousData,
    queryFn: () => getTemplate({ deviceType, isUnity, key, version }),
    queryKey: templatesKeys.details(key, version),
    ...settings,
  })

  return { ...query, isLoadingAndEnabled: query.isLoading && !!settings.enabled }
}

export const useTemplateVersionsQuery = (
  key: string,
  settings?: { enabled?: boolean }
): UseQueryResult<ITemplate[]> => {
  return useQuery({
    placeholderData: keepPreviousData,
    queryFn: () => getTemplateVersions(key),
    queryKey: templatesKeys.versions(key),
    ...settings,
  })
}
